import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/** Matches COMPANY_VOCABULARY_MAX_ENTRIES on the server, which rejects anything longer. */
const MAX_ENTRIES = 100;

/**
 * Edits one company vocabulary — the closed set of values members pick from for a profile
 * field. Closed rather than free text so reporting can group by it: "EMEA" and "emea "
 * typed by two people would be two cohorts, and both could then fall under the
 * minimum-cohort threshold and vanish from the dashboard.
 */
export default function Vocabulary({
  label,
  values,
  onChange,
  disabled,
}: {
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const inputId = `leadhr-vocab-${label.replace(/\s+/g, '-').toLowerCase()}`;

  const add = () => {
    const entry = draft.trim();
    setDraft('');
    if (!entry || values.includes(entry) || values.length >= MAX_ENTRIES) {
      return;
    }
    onChange([...values, entry]);
  };

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={inputId} className="text-sm font-semibold">
        {label}
      </label>
      {values.length > 0 ? (
        <ul className="m-0 flex list-none flex-wrap gap-2 p-0" aria-label={label}>
          {values.map((value) => (
            <li
              key={value}
              className="flex items-center gap-1.5 rounded-lg px-3 py-[5px] text-[13px] leading-[1.3]"
              style={{ background: 'var(--lh-sand)' }}
            >
              <span>{value}</span>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange(values.filter((entry) => entry !== value))}
                aria-label={`${t('com_ui_leadhr_vocabulary_remove')}: ${value}`}
                className="cursor-pointer border-none bg-transparent p-0 leading-none"
                style={{ color: 'var(--lh-muted)' }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <input
        id={inputId}
        value={draft}
        disabled={disabled || values.length >= MAX_ENTRIES}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={add}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            add();
          }
        }}
        placeholder={t('com_ui_leadhr_vocabulary_placeholder')}
        className="lh-input max-w-md"
      />
    </div>
  );
}
